import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMobileScreen } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import {
  haptic,
  hapticsDiagnostics,
  isHapticsEnabled,
  isHapticsSupported,
  setHapticsEnabled,
} from "../../utils/haptics";
import { isIOS, isStandalone } from "../../utils/pwa";
import { IosHapticOverlay } from "../../ui/components/IosHapticOverlay";
import styles from "./TelegramSettings.module.css";

/**
 * Per-device haptics toggle. Its own panel because the general PreferencesBox
 * is gated behind ?p=1 and a backend fetch, which kept this control invisible.
 */
const HapticsSettings: React.FC = () => {
  const [on, setOn] = useState<boolean>(() => isHapticsEnabled());
  const [showDetails, setShowDetails] = useState(false);
  const supported = isHapticsSupported();
  const diag = hapticsDiagnostics();
  const iosInBrowser = isIOS() && !isStandalone();

  const onToggle = (checked: boolean) => {
    setHapticsEnabled(checked);
    setOn(checked);
    if (checked) haptic("medium");
    toast.success(`Haptics ${checked ? "enabled" : "disabled"}`, {
      position: "bottom-center",
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <FontAwesomeIcon icon={faMobileScreen} className={styles.titleIcon} />
        Haptics
      </div>
      <p className={styles.description}>
        A small buzz when you tap buttons and menus. Saved per device, so your phone and your
        laptop can differ.
      </p>

      <label className={styles.toggleRow}>
        <input type="checkbox" checked={on} onChange={(e) => onToggle(e.target.checked)} />
        <span>Haptic feedback</span>
      </label>

      {on && (
        <button
          type="button"
          className={`${styles.connectButton} ios-haptic-host`}
          onClick={() => haptic("success")}
        >
          <IosHapticOverlay />
          Test buzz
        </button>
      )}

      {diag.platform === "ios" && on && (
        <p className={styles.description} style={{ marginTop: 12 }}>
          On iPhone the buzz rides on a real switch control under your finger, so it fires on the
          tap itself. {iosInBrowser && "It needs the installed app, not a Safari tab."} Needs iOS
          17.4 or newer with System Haptics on in Settings → Sounds &amp; Haptics.
        </p>
      )}

      {!supported && (
        <p className={styles.description}>
          This device has no haptic support the web can reach, so the setting does nothing here.
        </p>
      )}

      <button
        type="button"
        className={styles.detailsToggle}
        onClick={() => setShowDetails((s) => !s)}
      >
        {showDetails ? "Hide device details" : "Show device details"}
      </button>
      {showDetails && (
        <pre className={styles.diagBlock}>
          {[
            `platform:        ${diag.platform}`,
            `iOS version:     ${diag.iosVersion ?? "n/a"}`,
            `installed app:   ${diag.standalone ? "yes" : "no (browser tab)"}`,
            `vibrate API:     ${diag.hasVibrateApi ? "yes" : "no"}`,
            `switch detected: ${diag.switchPropertyDetected ? "yes" : "no"}`,
            `transport:       ${diag.transport}`,
            `enabled:         ${diag.enabled ? "yes" : "no"}`,
          ].join("\n")}
        </pre>
      )}
    </div>
  );
};

export default HapticsSettings;
